"use client";

import React from "react";
import { useRouter } from "next/navigation";
import { User, LogOut, Settings } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/hooks/useAuth";
import { cn } from "@/lib/utils";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

interface UserProfileProps {
  isCollapsed?: boolean;
  className?: string;
}

export function UserProfile({
  isCollapsed = false,
  className,
}: UserProfileProps) {
  const router = useRouter();
  const { user, logout } = useAuth();

  // 사용자 정보가 없으면 기본값 사용
  const displayName = user?.mgname || "관리자";
  const role = user?.roleText || (user?.mgtype === 9 ? "본사" : "지사");
  const avatarUrl = user?.mgemail
    ? `https://www.gravatar.com/avatar/${hashEmail(user.mgemail)}?d=mp`
    : undefined;

  // 이메일 해싱 함수 (Gravatar용)
  function hashEmail(email: string): string {
    // 간단한 해시 구현 (실제로는 MD5 사용이 권장됨)
    return btoa(email)
      .replace(/[^a-z0-9]/gi, "")
      .substring(0, 32);
  }

  // 로그아웃 처리
  const handleLogout = async () => {
    await logout();
  };

  // 프로필 설정 페이지로 이동
  const handleProfileSettings = () => {
    router.push("/settings/profile");
  };

  return (
    <div
      className={cn(
        "flex items-center border-b border-border",
        isCollapsed ? "justify-center px-1 py-0" : "px-4 py-4",
        className
      )}
    >
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <div
            className={cn(
              "flex items-center cursor-pointer",
              isCollapsed ? "justify-center" : "gap-3"
            )}
          >
            <Avatar
              className={cn(
                "border-2 border-primary/20",
                isCollapsed ? "h-6 w-6" : "h-10 w-10"
              )}
            >
              <AvatarImage src={avatarUrl} alt={displayName} />
              <AvatarFallback>
                <User className={isCollapsed ? "h-4 w-4" : "h-5 w-5"} />
              </AvatarFallback>
            </Avatar>

            {/* 펼쳐진 상태에서만 이름과 정보 표시 */}
            {!isCollapsed && (
              <div className="flex flex-col overflow-hidden">
                <p className="font-medium text-sm truncate text-foreground">
                  {displayName}
                </p>
                <p className="text-xs text-muted-foreground truncate">{role}</p>
              </div>
            )}
          </div>
        </DropdownMenuTrigger>

        <DropdownMenuContent
          align={isCollapsed ? "center" : "start"}
          className="w-56"
        >
          <DropdownMenuLabel>
            <div className="flex flex-col space-y-1">
              <p className="text-sm font-medium">{displayName}</p>
              {user?.mgemail && (
                <p className="text-xs text-muted-foreground truncate">
                  {user.mgemail}
                </p>
              )}
              <p className="text-xs text-muted-foreground">
                {user?.comname ? `${role} / ${user.comname}` : role}
              </p>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleProfileSettings}>
            <Settings className="mr-2 h-4 w-4" />
            <span>관리자 정보 수정</span>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleLogout}>
            <LogOut className="mr-2 h-4 w-4" />
            <span>로그아웃</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
